import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import type { Shop, ShopAttribute } from '../chat/types.js';
import { sanitizeUrl, dispatchChatMarianaLinks } from '../../utils/chat-utils.js';
import styles from '../../styles/mariana-shop-detail-sheet.css?inline';
import { getPostalAdminLine, getFullScheduleLines, getDistanceLabel, renderShopStatusTag, isOriginalColorAmenity, getBrandsWithLogo, getShopImage } from './shop-detail-utils.js';
import './mariana-scroll-arrows.js';

const DISMISS_THRESHOLD_PX = 120;
const CLOSE_ANIMATION_MS = 1500;
const DRAG_CLOSE_ANIMATION_MS = 300;

@customElement('mariana-shop-detail-sheet')
export class MarianaShopDetailSheet extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: Object })
  shop: Shop | null = null;

  @property({ type: Boolean })
  open = false;

  @property({ type: String })
  source = '';

  @state() private isDragging = false;
  @state() private dragOffset = 0;
  @state() private closing = false;
  @state() private dragDismissing = false;
  @state() private servicesScrollIndex = 0;
  @state() private amenitiesScrollIndex = 0;
  @state() private brandsScrollIndex = 0;
  @state() private servicesHasOverflow = false;
  @state() private amenitiesHasOverflow = false;
  @state() private brandsHasOverflow = false;

  private readonly servicesContainerRef = createRef<HTMLElement>();
  private readonly amenitiesContainerRef = createRef<HTMLElement>();
  private readonly brandsContainerRef = createRef<HTMLElement>();

  private pointerStartY = 0;
  private closeTimeout?: ReturnType<typeof setTimeout>;
  private servicesScrollTimeout?: ReturnType<typeof setTimeout>;
  private amenitiesScrollTimeout?: ReturnType<typeof setTimeout>;
  private brandsScrollTimeout?: ReturnType<typeof setTimeout>;

  private get isAppCC(): boolean {
    return this.source.toLowerCase() === 'appcc';
  }

  connectedCallback(): void {
    super.connectedCallback();
    const vv = globalThis.visualViewport;
    if (vv) {
      vv.addEventListener('scroll', this.handleVisualViewportChange);
      vv.addEventListener('resize', this.handleVisualViewportChange);
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleKeydown);
    clearTimeout(this.closeTimeout);

    const vv = globalThis.visualViewport;
    if (vv) {
      vv.removeEventListener('scroll', this.handleVisualViewportChange);
      vv.removeEventListener('resize', this.handleVisualViewportChange);
    }
  }

  /**
   * Re-pin the sheet host to the actual visible viewport, mirroring
   * mariana-widget.ts's handleVisualViewportChange: on iOS the layout
   * viewport can scroll/shrink independently of the visual one, which
   * otherwise pushes the footer's safe-area padding over the content.
   */
  private readonly handleVisualViewportChange = (): void => {
    if (globalThis.innerWidth > 768) return;

    const vv = globalThis.visualViewport;
    if (!vv) return;

    // A high threshold (~keyboard height) so the browser chrome auto-hiding/showing
    // during an ordinary scroll (usually 50-100px) doesn't trigger a resize mid-gesture,
    // which would cancel the touch scroll the user is doing at that exact moment.
    if (vv.offsetTop > 0 || vv.height < globalThis.innerHeight - 180) {
      this.style.top = `${vv.offsetTop}px`;
      this.style.height = `${vv.height}px`;
      this.style.maxHeight = `${vv.height}px`;
      this.style.minHeight = `${vv.height}px`;
    } else {
      this.style.top = '';
      this.style.height = '';
      this.style.maxHeight = '';
      this.style.minHeight = '';
    }
  };

  protected updated(changed: Map<string, unknown>): void {
    this.updateOverflowState();

    if (!changed.has('open')) return;

    if (this.open) {
      clearTimeout(this.closeTimeout);
      this.closing = false;
      this.dragDismissing = false;
      document.addEventListener('keydown', this.handleKeydown);
      requestAnimationFrame(() => {
        this.renderRoot.querySelector<HTMLElement>('.sheet-panel')?.focus({ preventScroll: true });
      });
      return;
    }

    document.removeEventListener('keydown', this.handleKeydown);
    if (this.closing || this.dragDismissing) return;
    this.startCloseAnimation();
  }

  private hasOverflow(container: HTMLElement | undefined): boolean {
    return !!container && container.scrollWidth > container.clientWidth + 1;
  }

  private updateOverflowState(): void {
    this.servicesHasOverflow = this.hasOverflow(this.servicesContainerRef.value);
    this.amenitiesHasOverflow = this.hasOverflow(this.amenitiesContainerRef.value);
    this.brandsHasOverflow = this.hasOverflow(this.brandsContainerRef.value);
  }

  private startCloseAnimation(): void {
    this.closing = true;
    this.isDragging = false;
    this.dragOffset = 0;
    clearTimeout(this.closeTimeout);
    this.closeTimeout = setTimeout(() => {
      this.closing = false;
      this.dispatchEvent(new CustomEvent('shop-sheet-closed', { bubbles: true, composed: true }));
    }, CLOSE_ANIMATION_MS);
  }

  private requestClose(): void {
    this.dispatchEvent(new CustomEvent('shop-sheet-close', { bubbles: true, composed: true }));
  }

  private finishDragDismiss(): void {
    this.dragDismissing = true;

    const panel = this.renderRoot.querySelector<HTMLElement>('.sheet-panel');
    const targetOffset = (panel?.getBoundingClientRect().height ?? window.innerHeight) + 40;

    requestAnimationFrame(() => {
      this.dragOffset = targetOffset;
    });

    clearTimeout(this.closeTimeout);
    this.closeTimeout = setTimeout(() => {
      this.dragOffset = 0;
      this.requestClose();
      this.dispatchEvent(new CustomEvent('shop-sheet-closed', { bubbles: true, composed: true }));
    }, DRAG_CLOSE_ANIMATION_MS);
  }

  private readonly handleKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.requestClose();
  };

  private readonly handleOverlayClick = (): void => {
    this.requestClose();
  };

  private readonly handlePointerDown = (e: PointerEvent): void => {
    this.isDragging = true;
    this.pointerStartY = e.clientY;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  private readonly handlePointerMove = (e: PointerEvent): void => {
    if (!this.isDragging) return;
    this.dragOffset = Math.max(0, e.clientY - this.pointerStartY);
  };

  private readonly handlePointerUp = (): void => {
    if (!this.isDragging) return;
    this.isDragging = false;

    if (this.dragOffset > DISMISS_THRESHOLD_PX) {
      this.finishDragDismiss();
      return;
    }

    this.dragOffset = 0;
  };

  private readonly handleImageClick = (): void => {
    if (!this.shop) return;
    const pageUrl = this.shop.page_url;
    if (!pageUrl) return;

    if (this.isAppCC) {
      dispatchChatMarianaLinks(pageUrl);
      return;
    }

    window.open(pageUrl, '_blank');
  };

  private readonly handleDirectionsClick = (): void => {
    if (!this.shop) return;
    const url = sanitizeUrl(this.shop.directions_url || '');
    if (!url) return;

    if (this.isAppCC) {
      dispatchChatMarianaLinks(url);
      return;
    }

    window.open(url, '_blank');
  };

  private readonly handleHorizontalWheel = (e: WheelEvent): void => {
    const container = e.currentTarget as HTMLElement;
    if (container.scrollWidth <= container.clientWidth) return;
    if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;

    e.preventDefault();
    container.scrollLeft += e.deltaY;
  };

  private computeRowIndex(container: HTMLElement, itemClass: string): number {
    const items = Array.from(container.querySelectorAll<HTMLElement>(`.${itemClass}`));
    if (!items.length) return 0;

    const { left, width, right } = container.getBoundingClientRect();
    const center = left + width / 2;

    const firstRect = items[0].getBoundingClientRect();
    if (firstRect.left >= left - 10) return 0;

    const lastRect = items[items.length - 1].getBoundingClientRect();
    if (lastRect.right <= right + 10) return items.length - 1;

    return items.reduce((closest, item, index) => {
      const { left: itemLeft, width: itemWidth } = item.getBoundingClientRect();
      const itemCenter = itemLeft + itemWidth / 2;
      const distance = Math.abs(itemCenter - center);

      const { left: prevLeft, width: prevWidth } = items[closest].getBoundingClientRect();
      const prevCenter = prevLeft + prevWidth / 2;

      return distance < Math.abs(prevCenter - center) ? index : closest;
    }, 0);
  }

  private scrollRowToIndex(container: HTMLElement, index: number): void {
    const items = Array.from(container.children) as HTMLElement[];
    const clamped = Math.max(0, Math.min(index, items.length - 1));
    items[clamped]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  private readonly handleServicesScroll = (e: Event): void => {
    const container = e.currentTarget as HTMLElement;
    clearTimeout(this.servicesScrollTimeout);
    this.servicesScrollTimeout = setTimeout(() => {
      this.servicesScrollIndex = this.computeRowIndex(container, 'sheet-chip');
    }, 50);
  };

  private readonly handleAmenitiesScroll = (e: Event): void => {
    const container = e.currentTarget as HTMLElement;
    clearTimeout(this.amenitiesScrollTimeout);
    this.amenitiesScrollTimeout = setTimeout(() => {
      this.amenitiesScrollIndex = this.computeRowIndex(container, 'sheet-chip');
    }, 50);
  };

  private readonly handleBrandsScroll = (e: Event): void => {
    const container = e.currentTarget as HTMLElement;
    clearTimeout(this.brandsScrollTimeout);
    this.brandsScrollTimeout = setTimeout(() => {
      this.brandsScrollIndex = this.computeRowIndex(container, 'sheet-brand');
    }, 50);
  };

  private readonly handleServicesArrowClick = (e: CustomEvent<{ index: number }>): void => {
    const container = this.servicesContainerRef.value;
    if (!container) return;
    this.servicesScrollIndex = e.detail.index;
    this.scrollRowToIndex(container, e.detail.index);
  };

  private readonly handleAmenitiesArrowClick = (e: CustomEvent<{ index: number }>): void => {
    const container = this.amenitiesContainerRef.value;
    if (!container) return;
    this.amenitiesScrollIndex = e.detail.index;
    this.scrollRowToIndex(container, e.detail.index);
  };

  private readonly handleBrandsArrowClick = (e: CustomEvent<{ index: number }>): void => {
    const container = this.brandsContainerRef.value;
    if (!container) return;
    this.brandsScrollIndex = e.detail.index;
    this.scrollRowToIndex(container, e.detail.index);
  };

  private renderChipSection(title: string, items: ShopAttribute[] | undefined, variant: 'services' | 'amenities') {
    if (!items?.length) return '';

    const containerRef = variant === 'services' ? this.servicesContainerRef : this.amenitiesContainerRef;
    const scrollIndex = variant === 'services' ? this.servicesScrollIndex : this.amenitiesScrollIndex;
    const handleScroll = variant === 'services' ? this.handleServicesScroll : this.handleAmenitiesScroll;
    const handleArrowClick = variant === 'services' ? this.handleServicesArrowClick : this.handleAmenitiesArrowClick;
    const hasOverflow = variant === 'services' ? this.servicesHasOverflow : this.amenitiesHasOverflow;

    return html`
      <div class="sheet-section">
        <h3 class="sheet-section-title">${title}</h3>
        <div class="sheet-scroll-wrapper">
          <div class="sheet-chips-scroll" ${ref(containerRef)} @wheel=${this.handleHorizontalWheel} @scroll=${handleScroll}>
            ${items.map(item => html`
              <div class="sheet-chip">
                <div class="sheet-chip-icon">
                  ${item.image
          ? html`<img
                      src="${item.image}"
                      alt=""
                      class="sheet-chip-icon-img ${variant === 'amenities' && isOriginalColorAmenity(item.name) ? 'sheet-chip-icon-img--original-color' : ''}"
                    />`
          : html`<div class="sheet-chip-icon-placeholder"></div>`}
                </div>
                <span class="sheet-chip-label">${item.name || ''}</span>
              </div>
            `)}
          </div>
          ${hasOverflow ? html`
            <mariana-scroll-arrows
              .totalCards=${items.length}
              .currentIndex=${scrollIndex}
              @arrow-click=${handleArrowClick}
            ></mariana-scroll-arrows>
          ` : ''}
        </div>
      </div>
    `;
  }

  private renderBrandsSection(brands: ShopAttribute[] | undefined) {
    const brandsWithLogo = getBrandsWithLogo(brands);
    if (!brandsWithLogo.length) return '';

    return html`
      <div class="sheet-section">
        <h3 class="sheet-section-title">Marcas</h3>
        <div class="sheet-scroll-wrapper">
          <div class="sheet-brands-scroll" ${ref(this.brandsContainerRef)} @wheel=${this.handleHorizontalWheel} @scroll=${this.handleBrandsScroll}>
            ${brandsWithLogo.map(brand => html`
              <div class="sheet-brand">
                <img src="${brand.image}" alt="${brand.name || ''}" class="sheet-brand-logo" />
              </div>
            `)}
          </div>
          ${this.brandsHasOverflow ? html`
            <mariana-scroll-arrows
              .totalCards=${brandsWithLogo.length}
              .currentIndex=${this.brandsScrollIndex}
              @arrow-click=${this.handleBrandsArrowClick}
            ></mariana-scroll-arrows>
          ` : ''}
        </div>
      </div>
    `;
  }

  render() {
    const shop = this.shop;
    if (!shop || (!this.open && !this.closing)) return html``;

    const image = getShopImage(shop);
    const hours = getFullScheduleLines(shop);
    const specialSchedule = shop.special_schedule?.trim();
    const distanceLabel = getDistanceLabel(shop);
    const postalAdminLine = getPostalAdminLine(shop);
    const directionsUrl = shop.directions_url;
    const panelStyle = (this.isDragging || this.dragDismissing) ? `transform: translateY(${this.dragOffset}px);` : '';
    const hasAttributeSections = !!(
      shop.services?.length || shop.amenities?.length || shop.brands?.some(brand => !!brand.image)
    );

    return html`
      <div class="sheet-overlay ${this.open ? 'sheet-overlay--visible' : ''}" @click=${this.handleOverlayClick}>
        <div
          class="sheet-panel ${this.open ? 'sheet-panel--open' : ''} ${this.isDragging ? 'sheet-panel--dragging' : ''} ${this.dragDismissing ? 'sheet-panel--drag-closing' : ''}"
          style="${panelStyle}"
          role="dialog"
          aria-modal="true"
          aria-label="Detalhe da loja"
          tabindex="-1"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div
            class="sheet-drag-area"
            @pointerdown=${this.handlePointerDown}
            @pointermove=${this.handlePointerMove}
            @pointerup=${this.handlePointerUp}
            @pointercancel=${this.handlePointerUp}
          >
            <div class="sheet-drag-handle"></div>
          </div>

          <div class="sheet-scroll-content">
            <div class="sheet-image-container">
              ${image ? html`
                <img
                  src="${image}"
                  alt="${shop.name || 'Loja'}"
                  class="sheet-image"
                  @click=${this.handleImageClick}
                  style="cursor: pointer;"
                />
              ` : html`
                <div class="sheet-image-placeholder" @click=${this.handleImageClick} style="cursor: pointer;">
                  <div class="sheet-image-placeholder-icon"></div>
                </div>
              `}
              ${distanceLabel ? html`
                <div class="shop-distance-chip">
                  <div class="shop-distance-icon"></div>
                  <span class="shop-distance-text">${distanceLabel}</span>
                </div>
              ` : ''}
            </div>

            <div class="sheet-header-info">
              <p class="sheet-shop-name">${shop.name || 'Sem nome'}</p>
              <p class="sheet-shop-address">${shop.street_line_1 || '-'}</p>
              ${postalAdminLine ? html`<p class="sheet-shop-address-extra">${postalAdminLine}</p>` : ''}

              ${(hours.length > 0 || specialSchedule) ? html`
                <div class="shop-hours-row">
                  <div class="shop-hours-icon"></div>
                  <div class="shop-hours-lines">
                    ${hours.map(line => html`<div class="shop-hours-line">${line}</div>`)}
                    ${specialSchedule ? html`<div class="shop-hours-line shop-hours-line--special">${specialSchedule}</div>` : ''}
                  </div>
                  ${renderShopStatusTag(shop)}
                </div>
              ` : ''}
            </div>

            ${hasAttributeSections ? html`<div class="sheet-divider"></div>` : ''}
            ${this.renderChipSection('Serviços', shop.services, 'services')}
            ${this.renderChipSection('Comodidades', shop.amenities, 'amenities')}
            ${this.renderBrandsSection(shop.brands)}
            ${hasAttributeSections && directionsUrl ? html`<div class="sheet-divider"></div>` : ''}
          </div>

          ${directionsUrl ? html`
            <div class="sheet-footer">
              <button class="sheet-directions-cta" type="button" @click=${this.handleDirectionsClick}>
                <div class="sheet-directions-icon"></div>
                Obter direções
              </button>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mariana-shop-detail-sheet': MarianaShopDetailSheet;
  }
}
